import ProfilePhoto from '@/components/Avatar';
import Loader from '@/components/Loader';
import {PencilSquareIcon} from '@heroicons/react/24/outline';
import {useUser} from '@lore/auth';
import {useConfig} from '@lore/config';
import {useQueryMutation} from '@lore/query-connect';
import * as filestack from 'filestack-js';
import PropTypes from 'prop-types';
import React from 'react';

AvatarUpload.propTypes = {
  onEdit: PropTypes.func,
  description: PropTypes.string,
};

/** @typedef {import('@/config/filestack').FilestackConfig} FilestackConfig */

export default function AvatarUpload(props) {
  const {onEdit, description} = props;
  /** @type {{ filestack: FilestackConfig }|null} */
  const config = useConfig();
  const currentUser = useUser();
  const userMutation = useQueryMutation('currentUser.updateSingleton', currentUser)
  const hasPhoto = Boolean(currentUser?.data?.avatarUrl);

  function onEditPhoto() {
    const {
      apiKey,
      imageMax,
      acceptImages
    } = config.filestack;

    const client = filestack.init(apiKey);
    const picker = client.picker({
      accept: acceptImages,
      imageMax: imageMax,
      uploadInBackground: false,
      onUploadDone: async function (result) {
        const uploadedFile = result.filesUploaded[0];

        if (uploadedFile) {
          try {
            await userMutation.mutateAsync({avatarUrl: uploadedFile.url});
            if (onEdit) onEdit(uploadedFile.url);
          } catch (err) {
            console.error('Avatar update failed:', err);
            // surface to user
          }
        }
      }
    });

    picker.open().catch(console.error);
  }

  if (!currentUser) {
    return <Loader/>;
  }

  return (
    <div>
      <h3 className="mb-4 text-cs-gray-900">{hasPhoto ? 'Profile photo' : 'Add a profile photo'}</h3>

      <ProfilePhoto
        className="mb-6"
        size={32}
        src={currentUser.data.avatarUrl}
      />

      <button type="button" className="flex items-center space-x-2 text-cs-blue-500 hover:opacity-60"
              onClick={onEditPhoto}>
        <PencilSquareIcon className="h-4 w-4" aria-hidden="true"/>
        <span className="text-sm">{hasPhoto ? 'Edit photo' : 'Add photo'}</span>
      </button>
      {description && (<div className="text-base text-gray-500">{description}</div>)}
    </div>
  );
}
